import dayjs from 'dayjs';
import { z } from 'zod';

import { CertificateType } from '@udacity/udacity-certificate';

import { gql } from '~/utils/gql';

const certificateDataSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  programTitle: z.string().nullable(),
  programKey: z.string(),
  semanticType: z.string().nullable(),
  companyLogo: z
    .object({
      name: z.string(),
      url: z.string(),
    })
    .nullable(),
  partnerLogos: z
    .array(
      z.object({
        name: z.string(),
        url: z.string(),
      })
    )
    .nullable(),
  contract: z
    .object({
      contractInitiativeLogo: z.string().optional().nullable(),
      signatureName: z.string().optional().nullable(),
      signatureTitle: z.string().optional().nullable(),
      signatureURL: z.string().optional().nullable(),
    })
    .nullable(),
  graduationDate: z.string().nullable(),
  svgUrl: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  confirmUrl: z.string(),
  type: z.nativeEnum(CertificateType).nullable(),
});

export const certificateSchema = z
  .object({
    data: z.object({
      certificate: certificateDataSchema,
    }),
  })
  .transform(({ data }) => {
    // Note: legacy cert. might not have a graduation date. If so, set graduationDate to today.
    const isLegacyCertificate = Boolean(data.certificate.pdfUrl);
    if (isLegacyCertificate && !data.certificate.graduationDate) {
      data.certificate.graduationDate = dayjs().format('MMMM DD, YYYY');
    }
    return data.certificate;
  });

export type Certificate = z.infer<typeof certificateSchema>;
export type CertificateResponse = z.input<typeof certificateSchema>;

export const certificateFragment = gql`
  fragment certificate on Certificate {
    firstName
    lastName
    programTitle
    programKey
    semanticType
    companyLogo {
      name
      url
    }
    partnerLogos {
      name
      url
    }
    contract {
      contractInitiativeLogo
      signatureName
      signatureTitle
      signatureURL
    }
    graduationDate
    svgUrl
    pdfUrl
    confirmUrl
    type
  }
`;

export const fetchCertificateByEnrollmentIdQuery = gql`
  query CertificateWithEnrollmentID($enrollmentId: String!) {
    certificate(enrollmentID: $enrollmentId) {
      ...certificate
    }
  }
  ${certificateFragment}
`;

export const fetchCertificateByTokenIdQuery = gql`
  query CertificateWithToken($token: String!) {
    certificate(token: $token) {
      ...certificate
    }
  }
  ${certificateFragment}
`;

export const certificateV2Schema = z
  .object({
    data: z
      .object({
        certificateForLearningPlan: z.object({
          backgroundImage: z.object({
            altText: z.string(),
            src: z.string(),
            styles: z.string(),
          }),
          completionDate: z.object({
            content: z.string(),
            styles: z.string(),
          }),
          confirmationLink: z
            .object({
              url: z.string(),
              styles: z.string(),
            })
            .nullable(),
          images: z.array(
            z.object({
              altText: z.string(),
              src: z.string(),
              styles: z.string(),
            })
          ),
          textBlocks: z.array(
            z.object({
              content: z.string(),
              styles: z.string(),
            })
          ),
          partnerLogosContainer: z
            .object({
              logos: z.array(
                z.object({
                  name: z.string(),
                  url: z.string(),
                })
              ),
              styles: z.string(),
            })
            .nullable(),
        }),
      })
      .nullable(),
    errors: z
      .array(
        z.object({
          message: z.string(),
          path: z.array(z.string()),
        })
      )
      .optional()
      .nullable(),
  })
  .transform(({ data }) => data?.certificateForLearningPlan ?? null);

export type CertificateV2 = z.infer<typeof certificateV2Schema>;
export type CertificateV2Response = z.input<typeof certificateV2Schema>;

export const fetchCertificateV2ByLearningPlanSubscriptionIdQuery = gql`
  query CertificateV2ByLPSubscriptionID($subscriptionId: String!) {
    certificateForLearningPlan(subscriptionId: $subscriptionId) {
      backgroundImage {
        altText
        src
        styles
      }
      completionDate {
        content
        styles
      }
      confirmationLink {
        url
        styles
      }
      images {
        altText
        src
        styles
      }
      textBlocks {
        content
        styles
      }
      partnerLogosContainer {
        logos {
          name
          url
        }
        styles
      }
    }
  }
`;

export function getFetchCertificatesQuery(enrollmentIds: string[]) {
  return gql`
    query FetchCertificates {
      ${enrollmentIds.map(
        (enrollmentId, i) => `
          certificate${i}: certificate(enrollmentID: "${enrollmentId}") {
            ...certificate
          }
        `
      )}
    }
    ${certificateFragment}
  `;
}

export const certificatesSchema = z
  .object({
    data: z.record(certificateDataSchema),
  })
  .transform(({ data }) => {
    const certificates = [];

    for (const [key, value] of Object.entries(data)) {
      if (!value) {
        continue;
      }
      certificates.push(value);
    }

    return certificates;
  });

export type Certificates = z.infer<typeof certificatesSchema>;
export type CertificatesResponse = z.input<typeof certificatesSchema>;
